// Local debug panel (`?offline&debug`): shortcuts to test bonuses, power-ups, parts, seasons,
// timing and the sounds in the local game. Only there; nothing of it exists online.
import type { LocalGame } from '../pkg/client_wasm.js';
import { el } from './dom.js';
import { POWERUPS } from './render.js';
import { SFX, type SfxName } from './sfx.js';
import { SLOTS } from './shop.js';
import { sound } from './sound.js';
import { BONUS_NAMES } from './underdog.js';

/** What the panel works on; the local game owns the loop. */
export interface DebugHost {
  game: () => LocalGame;
  tanks: () => number;
  /** A new round: season `Season::to_u8` (0 none, undefined: random as usual), effects bits (0 = all). */
  newRound: (season?: number, effects?: number) => void;
  /** Simulation speed (1 normal, 0 paused). */
  speed: number;
  /** Steps to run once while paused. */
  steps: number;
}

const SEASONS = ['none', 'Spring', 'Summer', 'Autumn', 'Winter'];
const TANK_NAMES = ['Red', 'Blue'];

/** Fills `panel` (left of the map) with the debug controls for `host`. */
export function debugPanel(panel: HTMLElement, host: DebugHost): void {
  let tank = 0;
  // A button gives the focus back at once: Space must keep firing, not press it again.
  const button = (text: string, act: () => void, title = '') =>
    el('button', {
      textContent: text,
      title,
      onclick: (e: MouseEvent) => {
        act();
        (e.currentTarget as HTMLElement).blur();
        render();
      },
    });
  const select = (options: string[], value = 0) => {
    const s = el('select', {}, ...options.map((o, i) => el('option', { value: String(i), textContent: o })));
    s.value = String(value);
    s.onchange = () => s.blur();
    return s;
  };
  const row = (label: string, ...items: (Node | string)[]) => el('div', { className: 'row' }, el('span', { className: 'k', textContent: label }), ...items);

  const parts = SLOTS.map((slot) => select(slot.parts.map((p) => p.name)));
  const powerup = select(POWERUPS.map((p) => p.name));
  const season = select(['random', ...SEASONS.slice(1), 'none']);
  const effects = select(['all', 'random 2']);
  // Every sound to listen to one by one (Wunsch User 2026-10-23), as loud as at the own tank.
  const sounds = Object.keys(SFX) as SfxName[];
  const soundSel = select(sounds);
  let open = true;

  function render(): void {
    const g = host.game();
    const names = [...Array(host.tanks()).keys()].map((i) => TANK_NAMES[i] ?? `Target ${i - 1}`);
    tank = Math.min(tank, names.length - 1);
    const head = el('h3', {}, el('button', { className: 'fold', textContent: open ? '▾ Debug' : '▸ Debug', onclick: () => ((open = !open), render()) }));
    if (!open) return void panel.replaceChildren(head);
    panel.replaceChildren(
      head,
      row('Tank', ...names.map((n, i) => button(i === tank ? `[${n}]` : n, () => (tank = i)))),
      row('Underdog', ...[1, 2, 3].map((b) => button(BONUS_NAMES[b], () => g.debug_bonus(tank, b)))),
      row(
        'Tank',
        button('Kill', () => g.debug_kill(tank)),
        button('Heal', () => g.debug_heal(tank)),
        button(g.debug_is_god(tank) ? 'God: on' : 'God: off', () => g.debug_god(tank, !g.debug_is_god(tank)), 'cannot die'),
      ),
      ...SLOTS.map((slot, k) => row(slot.title, parts[k])),
      row('', button('Apply parts', () => g.debug_parts(tank, Uint8Array.from(parts.map((p) => Number(p.value)))))),
      row('Power-up', powerup),
      row('', button('In hand', () => g.debug_give(tank, Number(powerup.value))), button('Drop in front', () => g.debug_drop(tank, Number(powerup.value)))),
      row('Round', button('Skip countdown', () => g.debug_skip_countdown()), button('Zone off', () => g.debug_zone(0)), button('Zone now', () => g.debug_zone(1))),
      row('Season', season, effects),
      row(
        '',
        button('New round', () => {
          const v = Number(season.value);
          // random (0) as usual; a season (1–4) or none (5) with all or two random effects.
          if (v === 0) host.newRound();
          else host.newRound(v === 5 ? 0 : v, Number(effects.value) === 0 ? 0 : 255);
        }),
      ),
      row(
        'Sound',
        soundSel,
        button('Play', () => sound(sounds[Number(soundSel.value)])),
        button('Next', () => {
          soundSel.value = String((Number(soundSel.value) + 1) % sounds.length);
          sound(sounds[Number(soundSel.value)]);
        }),
      ),
      row(
        'Time',
        ...[1, 0.5, 0.25].map((s) => button(host.speed === s ? `[${s}×]` : `${s}×`, () => (host.speed = s))),
        button(host.speed === 0 ? '[Pause]' : 'Pause', () => (host.speed = 0)),
        button('Step', () => ((host.speed = 0), (host.steps += 1))),
      ),
    );
  }
  render();
}
